/**
 * Pure rules for OAuth client profiles - one copy shared by the admin form,
 * the Authentication Setup picker and the mock backend, so "which inputs a
 * check covers" and "which change needs review" can never disagree.
 */
import type {
  OAuthClientProfile,
  ProfileAction,
  ProfileInput,
  ProfilePurpose,
  ProfileRegistrationInput,
  ProfileValidation,
} from "@/api/temp/OAuthClientProfileService";

export const PROFILE_NAME_MAX = 80;
export const PROFILE_DESCRIPTION_MAX = 280;

/** The specification version the (mock) validator is pinned to. */
export const CIMD_SPECIFICATION_PROFILE =
  "draft-ietf-oauth-client-id-metadata-document-02";

export const PROFILE_PURPOSES: ProfilePurpose[] = [
  "METADATA_DISCOVERY",
  "RUNTIME_ACCESS",
];

const trimmed = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * The form's values reduced to the allowlisted write DTO. The unselected
 * registration branch is dropped entirely, so no hidden value from the other
 * method can reach a write.
 */
export interface ProfileFormValues extends Record<string, unknown> {
  name?: string;
  description?: string;
  environmentIds?: string[];
  purposes?: ProfilePurpose[];
  registrationKind?: "CIMD" | "EXISTING_OAUTH_CLIENT" | "";
  clientIdUrl?: string;
  clientId?: string;
  /** The provider integration the application is registered with. */
  provider?: { id: string; name: string } | string | null;
  /** A legacy record's saved issuer, kept as is until a provider is chosen. */
  legacyIssuer?: string;
  managedCredential?: { id: string; name: string } | string | null;
}

/** A dropdown value (`{ id, name }`) or a plain string, as a trimmed id. */
const optionId = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object") {
    return trimmed((value as Record<string, unknown>).id);
  }
  return "";
};

export function toProfileInput(values: ProfileFormValues): ProfileInput {
  const kind = values.registrationKind || "CIMD";
  const providerId = optionId(values.provider);
  // The URL is the client ID: stored exactly as entered (only outer spaces
  // are trimmed) - never normalized or repaired.
  const registration: ProfileRegistrationInput =
    kind === "CIMD"
      ? { kind, clientIdUrl: trimmed(values.clientIdUrl) || undefined }
      : {
          kind,
          clientId: trimmed(values.clientId) || undefined,
          ...(providerId
            ? { providerId }
            : { issuer: trimmed(values.legacyIssuer) || undefined }),
        };
  const credential = optionId(values.managedCredential);
  return {
    name: trimmed(values.name),
    description: trimmed(values.description) || undefined,
    environmentIds: [...(values.environmentIds ?? [])],
    purposes: [...(values.purposes ?? [])],
    registration,
    managedCredentialRef: credential || undefined,
  };
}

/** The external identity: what makes two records the same OAuth client. */
export function identityKey(
  registration: ProfileRegistrationInput | OAuthClientProfile["registration"],
): string | undefined {
  if (registration.kind === "CIMD") {
    return registration.clientIdUrl
      ? `cimd:${registration.clientIdUrl}`
      : undefined;
  }
  if (registration.kind === "EXISTING_OAUTH_CLIENT") {
    const bound = registration.providerId ?? registration.issuer;
    return registration.clientId && bound
      ? `client:${bound}|${registration.clientId}`
      : undefined;
  }
  return `legacy:${registration.sourceRef}`;
}

/**
 * The inputs a profile check depends on. Name, description, environment and
 * purposes are TrustLogix labels - changing them never invalidates a check.
 */
export function profileInputFingerprint(input: ProfileInput): string {
  const { registration } = input;
  const parts =
    registration.kind === "CIMD"
      ? [`url=${registration.clientIdUrl ?? ""}`]
      : [
          `client=${registration.clientId ?? ""}`,
          `provider=${registration.providerId ?? ""}`,
          `issuer=${registration.issuer ?? ""}`,
        ];
  return [
    registration.kind,
    ...parts,
    `cred=${input.managedCredentialRef ?? ""}`,
  ].join("|");
}

/** A check counts only for the exact inputs it ran against. */
export const isValidationCurrent = (
  validation: ProfileValidation | undefined,
  fingerprint: string,
): boolean =>
  !!validation &&
  validation.state === "PASSED" &&
  validation.inputFingerprint === fingerprint;

/** Minimum for a draft: a name within the length limit. */
export const isDraftSavable = (input: ProfileInput): boolean =>
  input.name.length > 0 && input.name.length <= PROFILE_NAME_MAX;

/** Everything a profile needs before its checks can make it available. */
export function isProfileComplete(input: ProfileInput): boolean {
  if (!isDraftSavable(input) || input.purposes.length === 0) return false;
  const { registration } = input;
  if (registration.kind === "CIMD") return !!registration.clientIdUrl;
  return (
    !!registration.clientId &&
    (!!registration.providerId || !!registration.issuer)
  );
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every(value => b.includes(value));

export function registrationInputOf(
  profile: OAuthClientProfile,
): ProfileRegistrationInput | undefined {
  const { registration } = profile;
  if (registration.kind === "CIMD") {
    return { kind: "CIMD", clientIdUrl: registration.clientIdUrl };
  }
  if (registration.kind === "EXISTING_OAUTH_CLIENT") {
    return {
      kind: "EXISTING_OAUTH_CLIENT",
      clientId: registration.clientId,
      ...(registration.providerId
        ? { providerId: registration.providerId }
        : { issuer: registration.issuer }),
    };
  }
  return undefined;
}

type ConsequentialField =
  | "managedCredentialRef"
  | "purposes"
  | "environmentIds";

/** Changes that can affect configurations already referencing the profile. */
export function consequentialChanges(
  profile: OAuthClientProfile,
  input: ProfileInput,
): ConsequentialField[] {
  const changes: ConsequentialField[] = [];
  if (
    (profile.clientAuthentication?.managedCredentialRef ?? "") !==
    (input.managedCredentialRef ?? "")
  ) {
    changes.push("managedCredentialRef");
  }
  if (!sameSet(profile.purposes, input.purposes)) changes.push("purposes");
  if (!sameSet(profile.environmentIds, input.environmentIds)) {
    changes.push("environmentIds");
  }
  return changes;
}

/** Whether the external OAuth client identity itself would change. */
export function identityChanges(
  profile: OAuthClientProfile,
  input: ProfileInput,
): boolean {
  // `identityKey` already encodes the registration kind.
  const current = registrationInputOf(profile);
  return !current || identityKey(current) !== identityKey(input.registration);
}

/** Failed, stale or incomplete - a computed filter, not a lifecycle state. */
const ATTENTION_CHECK_STATES = ["FAILED", "STALE", "UNAVAILABLE"];
const ATTENTION_CREDENTIAL_STATES = ["MISSING", "RESTRICTED"];

export const needsAttention = (profile: OAuthClientProfile): boolean =>
  profile.lifecycle !== "ARCHIVED" &&
  (ATTENTION_CHECK_STATES.includes(profile.validation.state) ||
    ATTENTION_CREDENTIAL_STATES.includes(
      profile.clientAuthentication?.availability ?? "",
    ) ||
    (profile.lifecycle === "DRAFT" &&
      profile.validation.state === "NOT_CHECKED"));

/** Whether this user may take `action` on this record. */
export const canPerform = (
  profile: OAuthClientProfile,
  action: ProfileAction,
  canManage: boolean,
): boolean => canManage && profile.allowedActions.includes(action);

/** The form's starting values for an existing profile (edit) or a new one. */
export function profileFormValuesOf(
  profile?: OAuthClientProfile,
  preset: Partial<ProfileFormValues> = {},
): ProfileFormValues {
  if (!profile) {
    return {
      name: "",
      description: "",
      environmentIds: [],
      purposes: [...PROFILE_PURPOSES],
      registrationKind: "",
      clientIdUrl: "",
      clientId: "",
      provider: null,
      legacyIssuer: "",
      managedCredential: null,
      ...preset,
    };
  }
  const { registration, clientAuthentication } = profile;
  return {
    name: profile.name,
    description: profile.description ?? "",
    environmentIds: [...profile.environmentIds],
    purposes: [...profile.purposes],
    registrationKind:
      registration.kind === "LEGACY_READ_ONLY" ? "" : registration.kind,
    clientIdUrl:
      registration.kind === "CIMD" ? (registration.clientIdUrl ?? "") : "",
    clientId:
      registration.kind === "EXISTING_OAUTH_CLIENT"
        ? (registration.clientId ?? "")
        : "",
    provider:
      registration.kind === "EXISTING_OAUTH_CLIENT" && registration.providerId
        ? {
            id: registration.providerId,
            name: registration.providerName ?? registration.providerId,
          }
        : null,
    legacyIssuer:
      registration.kind === "EXISTING_OAUTH_CLIENT" && !registration.providerId
        ? (registration.issuer ?? "")
        : "",
    managedCredential: clientAuthentication?.managedCredentialRef
      ? {
          id: clientAuthentication.managedCredentialRef,
          name:
            clientAuthentication.referenceLabel ??
            clientAuthentication.managedCredentialRef,
        }
      : null,
  };
}

/**
 * An issuer that can be shown, copied and compared: present, and not a
 * documented format still holding a placeholder such as `{tenant}`.
 */
export const isConcreteIssuer = (url: string | undefined): url is string =>
  !!url && !/[{}]/.test(url);
